import React, { useState } from "react";
import { Box, Flex, SimpleGrid, Skeleton, Text } from "@chakra-ui/react";
import { ManagerTrackRecordProps } from "./ManagerTrackRecord.types";
import { VeriSelect } from "../../form-controls";
import { VeriHeading } from "../../common/VeriHeading";
import { formatMoneyWithSymbol } from "../../../utils";
import { VeriHelpToolTip } from "../../popover/VeriHelpToolTip";
import useSWR from "swr";
import { TrackRecordList } from "./TrackRecordList";
import { InvestmentTypeEnumType } from "../../../api/enums";

export const ManagerTrackRecord: React.FC<ManagerTrackRecordProps> = ({
  manager,
  trackRecord,
  isEditing,
  ...rest
}) => {
  const [selectedType, setSelectedType] = useState("all");
  const [selectedInvestmentType, setSelectedInvestmentType] = useState("");
  const [selectedOpportunity, setSelectedOpportunity] = useState("");

  const { data: summaryData } = useSWR(
    manager?.id ? `/track-records/investment-summary/${manager.id}` : undefined,
    {
      focusThrottleInterval: 0,
    },
  );

  const { data: calculatedData } = summaryData || {};

  if (!manager || !trackRecord || trackRecord?.length === 0) {
    return <></>;
  }

  const opportunityTags = Array.from(
    new Set(
      (trackRecord || [])
        .filter((record) => !!record.opportunityTag)
        .map((record) => record.opportunityTag),
    ),
  );

  const investmentTypes = Array.from(
    new Set(
      (trackRecord || [])
        .filter((record) => !!record.investmentType)
        .map((record) => record.investmentType),
    ),
  );

  const clearAllFilters = () => {
    setSelectedType("all");
    setSelectedInvestmentType("");
    setSelectedOpportunity("");
  };

  // const calculatedData = calculateData(trackRecord);

  return (
    <Box {...rest}>
      <Box mb={8} mt={4}>
        <SimpleGrid columns={{ base: 1, md: 3 }}>
          <Box>
            <Flex>
              <VeriHeading textStyle={"labelSmall"} color={"text.greyDark"}>
                Amount of active investments
              </VeriHeading>
              <VeriHelpToolTip ml={2} marginTop={0.5}>
                <Box maxW={"396px"}>
                  <VeriHeading textStyle={"h6"} as={"span"} display={"block"}>
                    Amount of Active Investments
                  </VeriHeading>
                  <Text
                    textStyle={"bodyRegular"}
                    color={"text.grey"}
                    as={"span"}
                    whiteSpace={"normal"}
                    display={"block"}
                    mt={1}
                  >
                    The sum of (a) the total capitalized acquisition cost of any
                    real estate assets (including debt, equity, and cost of
                    improvements), (b) the total principal amount of any loans
                    issued by investment manager, and (c) any other amounts
                    identified by Verivest as reasonable to include in this
                    figure under the relevant circumstances.
                  </Text>
                </Box>
              </VeriHelpToolTip>
            </Flex>
            {!calculatedData ? (
              <Skeleton height="40px" mr={4} />
            ) : (
              <Text textStyle={"h4"} color={"text.black"} mt={2}>
                {formatMoneyWithSymbol(
                  calculatedData?.amountActiveInvestments,
                  2,
                )}
              </Text>
            )}
          </Box>
          <Box>
            <Flex>
              <VeriHeading textStyle={"labelSmall"} color={"text.greyDark"}>
                Amount of realized investments
              </VeriHeading>
              <VeriHelpToolTip ml={2} marginTop={0.5}>
                <Box maxW={"396px"}>
                  <VeriHeading textStyle={"h6"} as={"span"} display={"block"}>
                    Amount of Realized Investments
                  </VeriHeading>
                  <Text
                    textStyle={"bodyRegular"}
                    color={"text.grey"}
                    as={"span"}
                    whiteSpace={"normal"}
                    display={"block"}
                    mt={1}
                  >
                    The sum of (a) the gross sales prices of all real estate
                    assets sold, (b) the total principal amounts repaid by all
                    of investment manager’s borrowers, and (c) any other amounts
                    identified by Verivest as reasonable to include in this
                    figure under the relevant circumstances
                  </Text>
                </Box>
              </VeriHelpToolTip>
            </Flex>
            {!calculatedData ? (
              <Skeleton height="40px" mr={4} />
            ) : (
              <Text textStyle={"h4"} color={"text.black"} mt={2}>
                {formatMoneyWithSymbol(
                  calculatedData?.amountRealizedInvestments,
                  2,
                )}
              </Text>
            )}
          </Box>
          {calculatedData && (
            <Box>
              <Flex>
                <VeriHeading textStyle={"labelSmall"} color={"text.greyDark"}>
                  Average hold period
                </VeriHeading>
                <VeriHelpToolTip ml={2} marginTop={0.5}>
                  <Box maxW={"396px"}>
                    <VeriHeading textStyle={"h6"} as={"span"} display={"block"}>
                      Average Hold Period
                    </VeriHeading>
                    <Text
                      textStyle={"bodyRegular"}
                      color={"text.grey"}
                      as={"span"}
                      whiteSpace={"normal"}
                      display={"block"}
                      mt={1}
                    >
                      The average length of time between the acquisition and
                      disposition of each asset investment manager has purchased
                      with investor capital or the time between initial loan and
                      full payoff of each loan made with investor capital.
                    </Text>
                  </Box>
                </VeriHelpToolTip>
              </Flex>
              {!calculatedData ? (
                <Skeleton height="40px" mr={4} />
              ) : (
                <Text textStyle={"h4"} color={"text.black"} mt={2}>
                  {calculatedData?.averageHoldingPeriod + " months"}
                </Text>
              )}
            </Box>
          )}
        </SimpleGrid>
      </Box>
      <Box>
        <Flex
          alignItems="center"
          flexWrap="wrap"
          justifyContent="space-between"
          mb={4}
        >
          <Flex alignItems={"flex-end"}>
            <VeriHeading textStyle="h6" mb={0} pb={0}>
              {trackRecord?.length || 0} investments{" "}
            </VeriHeading>
          </Flex>
          <Flex>
            {investmentTypes?.length > 1 && (
              <VeriSelect
                maxW={"228px"}
                w={"100%"}
                mr={4}
                onChange={(e) => setSelectedInvestmentType(e.target.value)}
                value={selectedInvestmentType}
                isSmall
              >
                <option value={""}>All investment types</option>
                {investmentTypes.map((type) => (
                  <option key={type} value={type || ""}>
                    {type}
                  </option>
                ))}
              </VeriSelect>
            )}
            {opportunityTags?.length > 1 && (
              <VeriSelect
                maxW={"228px"}
                w={"100%"}
                mr={4}
                onChange={(e) => setSelectedOpportunity(e.target.value)}
                value={selectedOpportunity}
                isSmall
              >
                <option value={""}>All opportunities</option>
                {opportunityTags.map((tag) => (
                  <option key={tag} value={tag || ""}>
                    {tag}
                  </option>
                ))}
              </VeriSelect>
            )}
            <VeriSelect
              onChange={(e) => setSelectedType(e.target.value)}
              value={selectedType}
              maxW={"228px"}
              w={"100%"}
              isSmall
            >
              <option value={"all"}>Active and Realized</option>
              <option value={"active"}>Active investments</option>
              <option value={"realized"}>Realized investments</option>
            </VeriSelect>
          </Flex>
        </Flex>

        <TrackRecordList
          status={selectedType}
          selectedInvestmentType={
            selectedInvestmentType as InvestmentTypeEnumType
          }
          selectedOpportunity={selectedOpportunity}
          trackRecord={trackRecord}
          isEditing={isEditing || false}
          clearAllFilters={clearAllFilters}
        />
      </Box>
    </Box>
  );
};
